// API Client
import CarbonClient from "../CarbonClient";

/**
 * AI usage feedback (the `/api/ai-usage` controller). The agent service records one usage row per
 * assistant turn under a `requestId` it mints and streams to the chat as the `data-interaction_start`
 * part; rating that turn is a write against the same row, so the score is joinable with the exact
 * prompt, model and token counts it judges. The row is matched on (user, tenant, requestId) server-side,
 * so a user can only ever rate their own responses.
 */

/** Integer 1..5 — the API rejects anything else. `1` is always worst, `5` always best. */
export const AI_RESPONSE_RATING_MIN = 1;
export const AI_RESPONSE_RATING_MAX = 5;
/** Server-side cap on the optional free-text comment. */
export const AI_RESPONSE_RATING_COMMENT_MAX_LENGTH = 2000;

export interface SubmitAiResponseRatingBody {
  /** The interaction id the agent streamed for the turn being rated. */
  requestId: string;
  rating: number;
  /** Optional. Omitting it CLEARS a comment stored by an earlier call — resend it when re-rating. */
  comment?: string | null;
}

/**
 * Stores (or overwrites) the rating for one assistant turn. Resolves on a 200 and throws otherwise —
 * a 404 means the usage row was never created, which is the case when usage tracking is disabled in
 * the environment the agent service runs in.
 */
export const submitAiResponseRating = async (body: SubmitAiResponseRatingBody): Promise<void> => {
  const response = await CarbonClient.post<{ recorded: boolean }>("api/ai-usage/feedback", {
    requestId: body.requestId,
    rating: body.rating,
    comment: body.comment?.trim() || null,
  });
  if (response.status !== 200) {
    throw new Error(`Failed to record AI response rating: ${response.status}`);
  }
};
